/* ==========================================================================
   Wonder Jacket — shared page chrome

   Two behaviours that belong to the whole storefront rather than to any one
   section: the back-to-top control and the newsletter popup.

   Replaces prev's back-to-top.liquid and newsletter-popup.liquid, both of
   which depended on jQuery and Magnific Popup. No dependencies here.
   ========================================================================== */

(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ------------------------------------------------------------------------
     Back to top

     Appears once the page has scrolled past a threshold set by the markup.
     Hidden from assistive tech while off-screen so it is never a dead tab
     stop, and it never steals focus on its own.
     ------------------------------------------------------------------------ */

  function initBackToTop() {
    var button = document.querySelector('[data-wj-back-to-top]');
    if (!button) return;

    var threshold = parseInt(button.getAttribute('data-threshold'), 10) || 600;
    var visible = false;
    var ticking = false;

    function apply() {
      var shouldShow = window.scrollY > threshold;
      if (shouldShow === visible) return;
      visible = shouldShow;
      button.classList.toggle('is-visible', visible);
      button.setAttribute('aria-hidden', visible ? 'false' : 'true');
      button.tabIndex = visible ? 0 : -1;
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        apply();
        ticking = false;
      });
    }

    button.addEventListener('click', function () {
      window.scrollTo({
        top: 0,
        behavior: prefersReducedMotion.matches ? 'auto' : 'smooth',
      });
      // Send focus somewhere sensible rather than leaving it on a control
      // that is about to hide itself.
      var target = document.querySelector('#MainContent') || document.body;
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    });

    window.addEventListener('scroll', onScroll, { passive: true });
    apply();
  }

  /* ------------------------------------------------------------------------
     Newsletter popup

     Opens once per session after a delay, unless the visitor has dismissed it
     or subscribed. Dismissal is remembered in localStorage; the once-per-
     session rule uses sessionStorage. Both are wrapped, because a browser set
     to block site data throws on access rather than returning null.

     Suppressed entirely on the cart and checkout paths — interrupting someone
     on their way to paying is the one place a popup costs money.
     ------------------------------------------------------------------------ */

  var DISMISS_KEY = 'wonder_newsletter_dismissed';
  var SESSION_KEY = 'wonder_newsletter_shown';

  function readStore(store, key) {
    try {
      return window[store].getItem(key);
    } catch (e) {
      return null;
    }
  }

  function writeStore(store, key, value) {
    try {
      window[store].setItem(key, value);
    } catch (e) {
      /* Private mode or blocked site data — the popup simply reappears. */
    }
  }

  function initNewsletterPopup() {
    var popup = document.querySelector('[data-wj-newsletter-popup]');
    if (!popup) return;

    var dialog = popup.querySelector('.wj-popup__dialog');
    var closers = popup.querySelectorAll('[data-wj-popup-close]');
    var delay = parseInt(popup.getAttribute('data-delay'), 10) || 6000;
    var lastFocused = null;

    if (window.Shopify && window.Shopify.designMode) {
      // In the theme editor the merchant needs to see it immediately and
      // repeatedly, so skip every suppression rule.
      delay = 400;
    } else {
      if (readStore('localStorage', DISMISS_KEY)) return;
      if (readStore('sessionStorage', SESSION_KEY)) return;
      if (/^\/(cart|checkout)/.test(window.location.pathname)) return;
    }

    function focusables() {
      return popup.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), textarea, select, [tabindex]:not([tabindex="-1"])'
      );
    }

    function open() {
      lastFocused = document.activeElement;
      popup.classList.add('is-open');
      popup.removeAttribute('aria-hidden');
      document.body.classList.add('wj-popup-open');
      writeStore('sessionStorage', SESSION_KEY, '1');

      var first = focusables()[0];
      if (first) first.focus();
    }

    function close(remember) {
      popup.classList.remove('is-open');
      popup.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('wj-popup-open');
      if (remember) writeStore('localStorage', DISMISS_KEY, '1');
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    }

    closers.forEach(function (el) {
      el.addEventListener('click', function () {
        close(true);
      });
    });

    // Clicking the scrim closes; clicking the dialog itself must not.
    popup.addEventListener('click', function (event) {
      if (!dialog.contains(event.target)) close(true);
    });

    popup.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        close(true);
        return;
      }
      if (event.key !== 'Tab') return;

      // Trap focus inside the dialog while it is open.
      var items = focusables();
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    // A successful signup should never prompt again.
    if (popup.querySelector('.wj-popup__success')) {
      writeStore('localStorage', DISMISS_KEY, '1');
      return;
    }

    window.setTimeout(open, delay);
  }

  function init() {
    initBackToTop();
    initNewsletterPopup();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
